import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { useAstroPetTRPC } from '@/lib/trpcContext';
import { inviteSearchSchema, splitInviteSearch } from '@/features/nutritionIntake/inviteSearch';
import { InviteResolverError } from '@/features/nutritionIntake/components/InviteResolverError';

/**
 * The address the invite link actually points at: `/nutrition-intake?appointmentId=…`.
 *
 * The link is minted by the workflow seed
 * (`packages/workflow/scripts/seed-hills-nutrition-intake-workflow.definition.ts`), which sets
 * `targetUrl` to this path and `dynamicParams: { appointmentId: '{{appointment.id}}' }`. The
 * appointment id is the run's `entityId`, which is the only identifier the engine holds at send
 * time — the submission row does not exist yet, so the message body has no id to name.
 *
 * ⚠️ **This page is a resolver, not a screen.** It swaps the appointment id for a submission id
 * and hands off to `/forms/:formResponseId`, which owns every state the form has. Rendering the
 * form here instead would mean a second copy of that route's loading, expiry and error handling.
 *
 * ⛔ **`replace`, so the appointment id leaves the history stack.** Back from the form would
 * otherwise land here and immediately bounce forward again, trapping the parent.
 *
 * ✅ **Click tracking is unaffected by this hop.** The click is recorded server-side by
 * `/t/:slug` (`apps/api/src/routes/trackedLinkRedirect.ts`), which writes
 * `WorkflowTrackedLinkClick` and stamps `firstClickedAt` before issuing its 302. Both hops here
 * happen in the browser, two steps downstream of a row that is already written. Likewise the
 * funnel: "Started" is stamped by `/forms/:id` against the run task, which reaches it because
 * the minted response carries `workflowRunTaskId`.
 *
 * ✅ **Every other query param is forwarded to the form**, not just the ones we can name.
 * `utm_source` and `utm_campaign` come from the link's `staticParams` and the SMS path appends a
 * `token`, and while nothing in pet-web reads any of them today — no analytics library,
 * `providers/auth.tsx` is a stub — a resolver that silently ate them would break the first
 * consumer that arrived, one hop before the page that wanted them and with no error to find.
 * Forwarding is unconditional so a param added to a future campaign's link needs no change here.
 *
 * ⚠️ **`appointmentId` and `sig` are the exceptions**, because this route consumes both. Carrying
 * them on would put them back in the URL bar, undoing the `replace` above and copying a
 * credential onto a URL with no use for it.
 *
 * ⛔ **`sig` is the HMAC that authorizes the appointment id** (`linkSignature.ts`), checked
 * server-side before any lookup. It is deliberately NOT gated on here: a browser-side
 * short-circuit on a tag-less link would turn "the campaign is minting unsigned links" into a
 * silent 100%-recipient failure with nothing in the server logs.
 */
function NutritionIntakeResolver(): React.JSX.Element {
  const { appointmentId, sig, passThrough } = splitInviteSearch(Route.useSearch());
  const trpc = useAstroPetTRPC();

  const { data, isPending, isError, error } = useQuery({
    ...trpc.nutritionForm.resolveByAppointment.queryOptions({ appointmentId, sig }),
    // ⛔ Gated on `appointmentId` ONLY, never on `sig`. Short-circuiting a tag-less link in the
    // browser would make a campaign that mints unsigned links fail for 100% of recipients with no
    // server-side signal at all — no `badSignature` WARN, no 4xx, just a funnel reading zero
    // starts. Letting the request through costs one rejected call and buys the log line that
    // names the problem. *(Caleb, #666.)*
    enabled: appointmentId !== '',
    // One attempt. The two outcomes are "this appointment has a form" and "it does not"; the
    // second is a NOT_FOUND that no amount of retrying changes, and the parent is staring at a
    // spinner while we do it.
    retry: false,
  });

  if (appointmentId === '') {
    return <InviteResolverError message="This link is missing information we need." />;
  }

  if (isPending) {
    return (
      <Flex justify="center" py={12}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (isError || data === undefined) {
    // Deliberately the same copy for "no such appointment" and "that appointment was never
    // enrolled" — the server answers NOT_FOUND for both so the page cannot become an oracle for
    // which appointment ids are real, and copy that distinguished them would undo that.
    return (
      <InviteResolverError
        message={error?.message ?? 'We could not open this form.'}
        description="Please try the link from your message again, or call your clinic."
      />
    );
  }

  return (
    <Navigate
      to="/forms/$formResponseId"
      params={{ formResponseId: data.formResponseId }}
      search={passThrough}
      replace
    />
  );
}

export const Route = createFileRoute('/nutrition-intake')({
  validateSearch: inviteSearchSchema,
  component: NutritionIntakeResolver,
});
